import {
  useContext,
  useState,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  useEffect,
} from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import "./styles.css";

import snd from "../../../languageConfig/local/snd";
import eng from "../../../languageConfig/local/en";
import ur from "../../../languageConfig/local/ur";
import romn from "../../../languageConfig/local/romn";
import {
  analytics,
  getLoginDesign,
  triggerLogEvent,
} from "../../../config/firebase";
import { getValidStoredLanguage, setLangInAsyncStorage } from "../../../constants";
import { sendOTP } from "../../../SyncService";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../context/SnackbarContext";
import { CircularProgress, Box } from "@mui/material";
import {
  CustomizesInvoice,
  MangeSaleExpense,
  StockUpdated,
  StreamlineAccounts,
} from "../../../assets/images";

// Lazy load variants for code splitting
const VariantA = lazy(() => import("./VariantA"));
const VariantB = lazy(() => import("./VariantB"));

const LanguageList = {
  en: {
    name: "en",
    display_name: "English",
  },
  romn: {
    name: "romn",
    display_name: "Roman urdu",
  },
  snd: {
    name: "snd",
    display_name: "سنڌي",
  },
  ur: {
    name: "ur",
    display_name: "اردو",
  },
};
const SignUp = ({
  handleSendOtpSuccess,
  handleSendOtpFailure,
  hasLockedAccount,
}) => {
  const lan = getValidStoredLanguage();
  const [error, setError] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [I18n, changeLanguage] = useContext(LanguageContext);
  const { showSnackbar } = useSnackbar();
  const [language, setLanguage] = useState(lan ? lan : "romn");
  const [langMenu, setLangMenu] = useState(
    LanguageList[lan || "romn"]?.display_name,
  );

  const [variant, setVariant] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const handleUpdateLoginVariant = async () => {
    const { udhaar_web_login_condition } = await getLoginDesign();
    setVariant(udhaar_web_login_condition ? "B" : "A");
    triggerLogEvent(
      analytics,
      `ab_test_variant_${udhaar_web_login_condition ? "B" : "A"}`,
    );
  };

  useEffect(() => {
    if (window.location.pathname === "/") {
      navigate({
        pathname: "/",
        search: location.search,
      }, { replace: true });
    }
  }, []);

  useEffect(() => {
    const phone = location?.state?.phoneNumber || "";
    if (phone) setPhone(phone);
    if (location?.state?.isReLogin && phone) {
      handleSubmit(null, true);
    }
  }, [location]);

  useEffect(() => {
    const phoneParam = new URLSearchParams(location.search).get("phone_number");
    if (!phoneParam) return;

    let digits = phoneParam.replace(/\D/g, "");
    if (digits.startsWith("92")) digits = "0" + digits.slice(2);

    const maxLength = digits.startsWith("0") ? 11 : 10;
    setPhone(digits.slice(0, maxLength));
    handlePhoneNumberChange(digits.slice(0, maxLength))
  }, []);

  useEffect(() => {
    handleUpdateLoginVariant();
  }, []);

  const sliderList = useMemo(
    () => [
      {
        img: MangeSaleExpense,
        title: I18n.your_sales_your_expenses_your_profit_crystal_clear,
      },
      {
        img: CustomizesInvoice,
        title: I18n.send_invoices_collect_payments_easy_free,
      },
      {
        img: StreamlineAccounts,
        title: I18n.know_who_owes_remind_automatically_get_paid_3x_faster,
      },
      {
        img: StockUpdated,
        title: I18n.keep_stock_updated_never_miss_a_sale_sell_smarter,
      },
    ],
    [
      I18n.your_sales_your_expenses_your_profit_crystal_clear,
      I18n.send_invoices_collect_payments_easy_free,
      I18n.know_who_owes_remind_automatically_get_paid_3x_faster,
      I18n.keep_stock_updated_never_miss_a_sale_sell_smarter,
    ],
  );


  // Optimize phone number change handler with useCallback
  const handlePhoneNumberChange = useCallback(
    (value) => {
      if (phone.length === 0 && value.length >= 1)
        triggerLogEvent(analytics, "login_number_entered");

      // Allow: 3XXXXXXXXX (10 digits) or 03XXXXXXXXX (11 digits)
      const maxLength = value.startsWith("0") ? 11 : 10;
      if (value.length > maxLength) return;

      const patt = /^0?3[0-9]{2}(?!1234567)(?!1111111)(?!7654321)[0-9]{7}$/;

      const targetLength = maxLength;
      if (value.length === targetLength && !patt.test(value)) {
        setError(I18n.phone_number_format);
        return;
      }

      setError("");
      setPhone(value);
    },
    [phone.length],
  );
  const cashbackAmount = location?.state?.cashbackAmount;

  // Optimize submit handler with useCallback
  const handleSubmit = useCallback(
    async (e, isReLogin) => {
      let tempPhone = isReLogin ? location?.state?.phoneNumber : phone;

      if (e) e.preventDefault();
      if ((!(tempPhone.length == (tempPhone.startsWith("0") ? 11 : 10)) || error)) {
        return;
      }
      setLoading(true);
      // Track A/B test conversion
      triggerLogEvent(analytics, "ab_test_submit_clicked", {
        variant: variant,
        page: "login",
      });

      tempPhone = tempPhone.startsWith("0") ? tempPhone : "0" + tempPhone;

      try {
        let params = {
          phone_number: tempPhone,
          version: "multi-business",
          onboarding_type: "login",
        };
        const OTPresponse = await sendOTP(params);
        setLoading(false);

        handleSendOtpSuccess({
          phone: tempPhone,
          OTPresponse: OTPresponse.data,
        });
        triggerLogEvent(analytics, "login_continue_existing", {
          ab_variant: variant,
        });
      } catch (error) {
        setLoading(false);
        if (
          error.response?.data?.account_status === "pin_reset_required" ||
          error.response?.data?.is_locked
        )
          hasLockedAccount();
        if (error.response?.data?.account_status === "suspended")
          hasLockedAccount(true);

        handleSendOtpFailure(error.response, tempPhone);
        if (
          error?.response?.data?.account_status === "active" &&
          error?.response?.data?.message
        ) {
          showSnackbar({
            message: error?.response?.data?.message,
            severity: "error",
            icon: "",
          });
        }
        if (error?.response?.status === 400) {
          triggerLogEvent(analytics, "login_continue_new", {
            ab_variant: variant,
          });
          localStorage.setItem("phone_number", tempPhone);
          navigate("/CreateBusiness", { state: { variant, cashbackAmount } });
        }
      }
    },
    [
      phone,
      variant,
      cashbackAmount,
      handleSendOtpSuccess,
      handleSendOtpFailure,
      showSnackbar,
      navigate,
    ],
  );

  // Optimize language selection with useCallback
  const chooseLanguage = useCallback(
    (lang) => {
      if (lang === "en" && language !== "en") {
        triggerLogEvent(analytics, "login_language_selected", {
          type: "English",
        });
        setLangMenu("English");
        changeLanguage(eng);
        localStorage.setItem("language", language);
      }
      if (lang === "ur" && language !== "ur") {
        triggerLogEvent(analytics, "login_language_selected", { type: "Urdu" });
        setLangMenu("اردو");
        changeLanguage(ur);
        localStorage.setItem("language", language);
      }
      if (lang === "snd" && language !== "snd") {
        triggerLogEvent(analytics, "login_language_selected", {
          type: "Sindhi",
        });
        setLangMenu("سنڌي");
        changeLanguage(snd);
        localStorage.setItem("language", language);
      }
      if (lang === "romn" && language !== "romn") {
        triggerLogEvent(analytics, "login_language_selected", {
          type: "Roman urdu",
        });
        setLangMenu("Roman urdu");
        changeLanguage(romn);
        localStorage.setItem("language", language);
      }
      setLanguage(lang);
      setLangInAsyncStorage(lang);
    },
    [language, changeLanguage],
  );

  // Memoize variant props to prevent unnecessary re-renders
  const variantProps = useMemo(
    () => ({
      sliderList,
      LanguageList,
      langMenu,
      chooseLanguage,
      I18n,
      error,
      phone,
      handlePhoneNumberChange,
      handleSubmit,
      loading,
      cashbackAmount,
    }),
    [
      sliderList,
      langMenu,
      chooseLanguage,
      I18n,
      error,
      phone,
      handlePhoneNumberChange,
      handleSubmit,
      loading,
      cashbackAmount,
    ],
  );

  // Lazy load component with Suspense fallback
  return (
    <Suspense>
      {variant ? (
        <VariantA {...variantProps} variant={variant} />
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          className="signupPageLoaderBox"
        >
          <CircularProgress />
        </Box>
      )}
    </Suspense>
  );
};

export default SignUp;
